import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "../apiClient";
import { useAuth } from "../hooks/useAuth";
import { useSubmissions } from "../hooks/useSubmissions";
import { STATUS_COLORS } from "./statusColors";
import CategorySidebar from "./CategorySidebar";

function ExercisesList() {
  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { submissions } = useSubmissions();
  const navigate = useNavigate();

  const handleAdminClick = () => {
    navigate('/admin');
  };


  useEffect(() => {
    const fetchExercisesAndPreferences = async () => {
      try {
        setLoading(true);

        // Fetch exercises
        const exercisesData = await apiClient.getExercises();

        // No category preference loading - selection doesn't persist per requirements
        
        // Map submissions to their respective exercises
        const submissionsMap = submissions.reduce((acc, submission) => {
          if (!acc[submission.exercise_id]) {
            acc[submission.exercise_id] = [];
          }
          acc[submission.exercise_id].push(submission.status);
          return acc;
        }, {});

        // Determine the status for each exercise
        const exercisesWithStatus = exercisesData.map((exercise) => {
          const exerciseSubmissions = submissionsMap[exercise.id] || [];
          const hasSuccess = exerciseSubmissions.includes("Success");
          let status = "Failed";

          if (exerciseSubmissions.length === 0) {
            status = "No submissions";
          } else if (hasSuccess) {
            status = "Success";
          }

          return {
            ...exercise,
            status,
          };
        });

        // Sort exercises by id
        exercisesWithStatus.sort((a, b) => a.id - b.id);

        setExercises(exercisesWithStatus);
        setFilteredExercises(exercisesWithStatus);
      } catch (error) {
        console.error("Error fetching exercises:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExercisesAndPreferences();
  }, [submissions, user]);

  useEffect(() => {
    if (selectedCategory === null || selectedCategory === 'All Categories') {
      setFilteredExercises(exercises);
    } else {
      const filtered = exercises.filter(exercise =>
        exercise.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
      setFilteredExercises(filtered);
    }
  }, [selectedCategory, exercises]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    // No persistence - selection resets on page refresh per requirements
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Easy":
        return { color: "green" }; // Green
      case "Medium":
        return { color: "#ffc107" }; // Yellow
      case "Hard":
        return { color: "red" }; // Red
      default:
        return {};
    }
  };

  const getStatusColor = (status) => STATUS_COLORS[status] || {};

  if (loading) {
    return <div>Loading exercises...</div>;
  }

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 40px)' }}>
      <CategorySidebar
        selectedCategory={selectedCategory}
        onCategorySelect={handleCategorySelect}
      />
      <div style={{ flex: 1, padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>
            Exercises List
            {selectedCategory && selectedCategory !== 'all' && ` - ${selectedCategory}`}
          </h1>
        </div>
        {filteredExercises.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <h3>No exercises available</h3>
            <p>There are no exercises in this category.</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Id</th>
                <th>Title</th>
                <th>Category</th>
                <th>Difficulty</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredExercises.map((exercise) => (
                <tr key={exercise.id}>
                  <td>{exercise.id}</td>
                  <td>
                    <Link to={`/exercise/${exercise.id}`}>{exercise.title_en}</Link>
                  </td>
                  <td>{exercise.category}</td>
                  <td style={getDifficultyColor(exercise.difficulty)}>
                    {exercise.difficulty}
                  </td>
                  <td style={getStatusColor(exercise.status)}>{exercise.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ExercisesList;
